import React, { useState, useEffect } from 'react';
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import DraggableNoteList from "@/components/DraggableNoteList";
import Menu from "@/components/Menu";
import ProfileSettings from "@/components/profile/ProfileSettings";
import Header from "@/components/home/Header";
import SearchBar from "@/components/home/SearchBar";
import GroupManager from "@/components/home/GroupManager";
import { NetworkProps } from '@/types/note';

interface Note {
  id: string;
  title: string;
  content: string;
  color: string;
  cover_image?: string;
  group_id?: string;
  updated_at?: string;
  is_public?: boolean;
  share_id?: string;
  comments?: any[];
}

interface Group {
  id: string;
  name: string;
  parent_id?: string;
}

const Home: React.FC<NetworkProps> = ({ isOnline }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [notes, setNotes] = useState<Note[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  const [subgroups, setSubgroups] = useState<Group[]>([]);
  const [selectedGroup, setSelectedGroup] = useState<string | null>(null);
  const [selectedSubgroup, setSelectedSubgroup] = useState<string | null>(null);
  const [showNewGroupDialog, setShowNewGroupDialog] = useState(false);
  const [newGroupName, setNewGroupName] = useState("");
  const [userEmail, setUserEmail] = useState<string>("");
  const [showSettings, setShowSettings] = useState(false);
  const [profile, setProfile] = useState<any>(null);
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    fetchUserData();
    fetchNotes();
    fetchGroups();
    fetchSubgroups();
  }, [selectedGroup, selectedSubgroup]);

  const handleSubgroupSelect = (subgroupId: string | null) => {
    console.log('Subgroup selected:', subgroupId);
    setSelectedSubgroup(subgroupId);
    // When selecting a subgroup, ensure its parent group remains selected
    if (subgroupId) {
      const subgroup = subgroups.find(sg => sg.id === subgroupId);
      if (subgroup) {
        const parentGroupName = subgroup.name.split(' > ')[0];
        const parentGroup = groups.find(g => g.name === parentGroupName);
        if (parentGroup && selectedGroup !== parentGroup.id) {
          setSelectedGroup(parentGroup.id);
        }
      }
    }
  };

  const fetchUserData = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      setUserEmail(user.email || "");
      const { data: profileData, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();
      
      if (error) {
        toast({
          title: "Error fetching profile",
          description: error.message,
          variant: "destructive",
        });
        return;
      }
      
      setProfile(profileData);
    }
  };

  const fetchNotes = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    let query = supabase
      .from('notes')
      .select('*')
      .eq('user_id', user.id);

    // Filter by selected group or subgroup
    const activeGroupId = selectedSubgroup || selectedGroup;
    if (activeGroupId) {
      query = query.eq('group_id', activeGroupId);
    }

    const { data, error } = await query.order('created_at', { ascending: false });

    if (error) {
      toast({
        title: "Error fetching notes",
        description: error.message,
        variant: "destructive",
      });
      return;
    }

    setNotes((data || []) as Note[]);
  };

  const fetchGroups = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { data, error } = await supabase
      .from('groups')
      .select('*')
      .eq('user_id', user.id);

    if (error) {
      toast({
        title: "Error fetching groups",
        description: error.message,
        variant: "destructive",
      });
      return;
    }

    // Filter out subgroups (those with " > " in the name) from main groups
    const mainGroups = data?.filter(group => !group.name.includes(' > ')) || [];
    setGroups(mainGroups);
  };

  const fetchSubgroups = async () => {
    // If neither group nor subgroup is selected, clear subgroups
    if (!selectedGroup && !selectedSubgroup) {
      setSubgroups([]);
      return;
    }

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    try {
      // Always fetch subgroups of the main group when a group is selected
      const mainGroupId = selectedGroup;
      if (!mainGroupId) return;

      // Try to fetch subgroups with hierarchical naming
      const { data, error } = await supabase
        .from('groups')
        .select('*')
        .eq('user_id', user.id);

      if (error) {
        // If parent_id column doesn't exist, look for groups with naming convention
        if (error.code === '42703' || error.code === 'PGRST204' || error.message.includes('parent_id')) {
          // Find the main group
          const mainGroup = groups.find(g => g.id === mainGroupId);
          
          if (mainGroup) {
            const { data: allGroups, error: fallbackError } = await supabase
              .from('groups')
              .select('*')
              .eq('user_id', user.id);

            if (fallbackError) {
              console.error('Error fetching groups for subgroup detection:', fallbackError);
              setSubgroups([]);
              return;
            }

            // Get all subgroups under the main group
            const mainGroupPattern = `${mainGroup.name} > `;
            console.log('Looking for subgroups with pattern:', mainGroupPattern);
            console.log('All groups:', allGroups);
            
            let foundSubgroups = allGroups?.filter(group => 
              group.name.startsWith(mainGroupPattern) && group.id !== mainGroupId
            ) || [];

            // If a subgroup is selected, also include any sub-subgroups
            if (selectedSubgroup) {
              const selectedSub = foundSubgroups.find(sg => sg.id === selectedSubgroup);
              if (selectedSub) {
                const subSubgroupPattern = `${selectedSub.name} > `;
                const subSubgroups = allGroups?.filter(group => 
                  group.name.startsWith(subSubgroupPattern) && 
                  group.id !== selectedSubgroup &&
                  !foundSubgroups.some(existing => existing.id === group.id)
                ) || [];
                foundSubgroups = [...foundSubgroups, ...subSubgroups];
              }
            }

            console.log('Found subgroups:', foundSubgroups);
            setSubgroups(foundSubgroups);
          } else {
            setSubgroups([]);
          }
        } else {
          console.error('Error fetching subgroups:', error);
          setSubgroups([]);
        }
        return;
      }

      // Filter for hierarchical subgroups based on naming pattern
      const mainGroup = groups.find(g => g.id === mainGroupId);
      if (mainGroup) {
        const mainGroupPattern = `${mainGroup.name} > `;
        let allSubgroups = data?.filter(group => 
          group.name.startsWith(mainGroupPattern) && group.id !== mainGroupId
        ) || [];

        // If a subgroup is selected, also include its children
        if (selectedSubgroup) {
          const selectedSub = allSubgroups.find(sg => sg.id === selectedSubgroup);
          if (selectedSub) {
            const subSubgroupPattern = `${selectedSub.name} > `;
            const subSubgroups = data?.filter(group => 
              group.name.startsWith(subSubgroupPattern) && 
              group.id !== selectedSubgroup &&
              !allSubgroups.some(existing => existing.id === group.id)
            ) || [];
            allSubgroups = [...allSubgroups, ...subSubgroups];
          }
        }
        setSubgroups(allSubgroups);
      } else {
        setSubgroups([]);
      }

    } catch (error) {
      console.error('Unexpected error in fetchSubgroups:', error);
      setSubgroups([]);
    }
  };

  const handleReorderGroups = async (reorderedGroups: Group[]) => {
    setGroups(reorderedGroups);
    
    // Update the created_at timestamps to maintain order
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      for (let i = 0; i < reorderedGroups.length; i++) {
        const group = reorderedGroups[i];
        const newTimestamp = new Date(Date.now() + i * 1000).toISOString();
        
        await supabase
          .from('groups')
          .update({ created_at: newTimestamp })
          .eq('id', group.id);
      }

      toast({
        title: "Groups reordered",
        description: "Group order has been updated successfully.",
      });
    } catch (error) {
      console.error('Error reordering groups:', error);
      toast({
        title: "Error",
        description: "Failed to save group order. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleReorderSubgroups = async (reorderedSubgroups: Group[]) => {
    setSubgroups(reorderedSubgroups);
    
    // Update the created_at timestamps to maintain order
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      for (let i = 0; i < reorderedSubgroups.length; i++) {
        const subgroup = reorderedSubgroups[i];
        const newTimestamp = new Date(Date.now() + i * 1000).toISOString();
        
        await supabase
          .from('groups')
          .update({ created_at: newTimestamp })
          .eq('id', subgroup.id);
      }

      toast({
        title: "Subgroups reordered",
        description: "Subgroup order has been updated successfully.",
      });
    } catch (error) {
      console.error('Error reordering subgroups:', error);
      toast({
        title: "Error",
        description: "Failed to save subgroup order. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleMoveSubgroup = async (subgroupId: string, newParentId: string) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    try {
      const subgroupToMove = subgroups.find(sg => sg.id === subgroupId);
      
      // Check if newParentId is a group or subgroup
      const newParentGroup = groups.find(g => g.id === newParentId);
      const newParentSubgroup = subgroups.find(sg => sg.id === newParentId);
      
      if (!subgroupToMove || (!newParentGroup && !newParentSubgroup)) {
        throw new Error('Subgroup or parent not found');
      }

      // Extract the subgroup's own name (last part after the last ">")
      const subgroupOwnName = subgroupToMove.name.split(' > ').pop();
      
      // Determine the new parent name based on whether it's a group or subgroup
      const newParentName = newParentGroup ? newParentGroup.name : newParentSubgroup?.name;
      const newSubgroupName = `${newParentName} > ${subgroupOwnName}`;

      // Update the subgroup name to reflect the new parent
      const { error } = await supabase
        .from('groups')
        .update({ name: newSubgroupName })
        .eq('id', subgroupId);

      if (error) {
        throw error;
      }

      // Also update any child subgroups if they exist
      const childSubgroups = subgroups.filter(sg => 
        sg.name.startsWith(`${subgroupToMove.name} > `)
      );
      
      for (const childSubgroup of childSubgroups) {
        const childOwnName = childSubgroup.name.split(' > ').slice(-1)[0];
        const newChildName = `${newSubgroupName} > ${childOwnName}`;
        
        await supabase
          .from('groups')
          .update({ name: newChildName })
          .eq('id', childSubgroup.id);
      }

      // Notes should stay with the moved subgroup (keep the same group_id)
      // No need to update notes' group_id since they should stay with their subgroup

      // Refresh data
      await fetchGroups();
      await fetchSubgroups();
      await fetchNotes();

    } catch (error) {
      console.error('Error moving subgroup:', error);
      toast({
        title: "Error",
        description: "Failed to move subgroup. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleConvertSubgroupToGroup = async (subgroupId: string) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    try {
      const subgroupToConvert = subgroups.find(sg => sg.id === subgroupId);
      
      if (!subgroupToConvert) {
        throw new Error('Subgroup not found');
      }

      // Extract the subgroup's own name (last part after the last ">")
      const subgroupOwnName = subgroupToConvert.name.split(' > ').pop();
      
      if (!subgroupOwnName) {
        throw new Error('Invalid subgroup name format');
      }

      // Update the subgroup to become a regular group
      const { error } = await supabase
        .from('groups')
        .update({ name: subgroupOwnName })
        .eq('id', subgroupId);

      if (error) {
        throw error;
      }

      // Update any child subgroups to reflect the new hierarchy
      const childSubgroups = subgroups.filter(sg => 
        sg.name.startsWith(`${subgroupToConvert.name} > `)
      );
      
      for (const childSubgroup of childSubgroups) {
        const remainingPath = childSubgroup.name.slice(`${subgroupToConvert.name} > `.length);
        const newChildName = `${subgroupOwnName} > ${remainingPath}`;
        
        await supabase
          .from('groups')
          .update({ name: newChildName })
          .eq('id', childSubgroup.id);
      }

      // Refresh data
      await fetchGroups();
      await fetchSubgroups();
      await fetchNotes();

      toast({
        title: "Subgroup converted to group",
        description: "The subgroup has been successfully converted to a main group.",
      });

    } catch (error) {
      console.error('Error converting subgroup to group:', error);
      toast({
        title: "Error",
        description: "Failed to convert subgroup to group. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleCreateGroup = async () => {
    if (!newGroupName.trim()) return;
    
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { error } = await supabase
      .from('groups')
      .insert([
        { name: newGroupName, user_id: user.id }
      ]);

    if (error) {
      toast({
        title: "Error creating group",
        description: error.message,
        variant: "destructive",
      });
      return;
    }

    toast({
      title: "Group created",
      description: "Your new group has been created successfully.",
    });

    setNewGroupName("");
    setShowNewGroupDialog(false);
    fetchGroups();
  };

  const handleDeleteGroup = async (groupId: string) => {
    const { error } = await supabase
      .from('groups')
      .delete()
      .eq('id', groupId);

    if (error) {
      toast({
        title: "Error deleting group",
        description: error.message,
        variant: "destructive",
      });
      return;
    }

    if (selectedGroup === groupId) {
      setSelectedGroup(null);
    }
    
    await fetchGroups();
    await fetchSubgroups();
  };

  const handleCreateSubgroup = async (parentId: string, name: string) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      toast({
        title: "Authentication error",
        description: "You must be logged in to create a subgroup.",
        variant: "destructive",
      });
      return;
    }

    if (!name.trim()) {
      toast({
        title: "Invalid name",
        description: "Subgroup name cannot be empty.",
        variant: "destructive",
      });
      return;
    }

    try {
      // Check if parentId is a main group or a subgroup
      const parentGroup = groups.find(g => g.id === parentId);
      const parentSubgroup = subgroups.find(sg => sg.id === parentId);
      
      let subgroupName: string;
      if (parentGroup) {
        // Creating subgroup under main group
        subgroupName = `${parentGroup.name} > ${name}`;
      } else if (parentSubgroup) {
        // Creating subgroup under existing subgroup
        subgroupName = `${parentSubgroup.name} > ${name}`;
      } else {
        toast({
          title: "Error",
          description: "Parent group not found.",
          variant: "destructive",
        });
        return;
      }
      
      console.log('Creating subgroup with naming convention:', subgroupName);
      
      const { data: createdGroup, error } = await supabase
        .from('groups')
        .insert([
          { name: subgroupName, user_id: user.id }
        ])
        .select();

      if (error) {
        console.error('Error creating subgroup:', error);
        toast({
          title: "Error creating subgroup",
          description: error.message || "Failed to create subgroup. Please try again.",
          variant: "destructive",
        });
        return;
      }
      
      console.log('Subgroup created successfully:', createdGroup);
      toast({
        title: "Subgroup created",
        description: `Created "${name}" successfully`,
      });

      // Refresh the data
      await fetchGroups();
      await fetchSubgroups();
    } catch (error) {
      console.error('Error in handleCreateSubgroup:', error);
      toast({
        title: "Unexpected error",
        description: "An unexpected error occurred. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleConvertGroupToSubgroup = async (groupId: string, newParentId: string) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    try {
      const groupToConvert = groups.find(g => g.id === groupId);
      if (!groupToConvert) {
        throw new Error('Group to convert not found');
      }

      // Find the new parent (could be group or subgroup)
      const newParentGroup = groups.find(g => g.id === newParentId);
      const newParentSubgroup = subgroups.find(sg => sg.id === newParentId);
      const newParentName = newParentGroup ? newParentGroup.name : newParentSubgroup?.name;
      
      if (!newParentName) {
        throw new Error('New parent not found');
      }

      const newSubgroupName = `${newParentName} > ${groupToConvert.name}`;

      // Update the group to become a subgroup with new name
      const { error: updateError } = await supabase
        .from('groups')
        .update({ name: newSubgroupName })
        .eq('id', groupId);

      if (updateError) {
        throw updateError;
      }

      // Update any existing subgroups of this group to maintain hierarchy
      const childSubgroups = subgroups.filter(sg => 
        sg.name.startsWith(`${groupToConvert.name} > `)
      );
      
      for (const childSubgroup of childSubgroups) {
        const remainingPath = childSubgroup.name.slice(`${groupToConvert.name} > `.length);
        const newChildName = `${newSubgroupName} > ${remainingPath}`;
        
        await supabase
          .from('groups')
          .update({ name: newChildName })
          .eq('id', childSubgroup.id);
      }

      // Refresh data
      await fetchGroups();
      await fetchSubgroups();
      await fetchNotes();

      toast({
        title: "Group converted",
        description: `"${groupToConvert.name}" has been converted to a subgroup.`,
      });
    } catch (error) {
      console.error('Error converting group to subgroup:', error);
      toast({
        title: "Error",
        description: "Failed to convert group. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/auth');
  };

  const handleNoteClick = (note: Note) => {
    navigate('/editor', { state: { note } });
  };

  const handleDeleteNote = async (id: string) => {
    const { error } = await supabase
      .from('notes')
      .delete()
      .eq('id', id);

    if (error) {
      toast({
        title: "Error deleting note",
        description: "Failed to delete note. Please try again.",
        variant: "destructive",
      });
      return;
    }

    setNotes(notes.filter(note => note.id !== id));
    toast({
      title: "Note deleted",
      description: "Your note has been deleted successfully.",
    });
  };

  const handleNotesReorder = async (reorderedNotes: Note[]) => {
    setNotes(reorderedNotes);
  };

  const filteredNotes = notes.filter(note => {
    const matchesSearch = note.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      note.content.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesSearch;
  });

  return (
    <div className="w-full max-w-7xl mx-auto px-4 py-8 pb-24" style={{ paddingLeft: 'max(1rem, env(safe-area-inset-left))', paddingRight: 'max(1rem, env(safe-area-inset-right))' }}>
      <Header
        userEmail={userEmail}
        profile={profile}
        onSettingsClick={() => setShowSettings(true)}
        onLogout={handleLogout}
      />

      <SearchBar
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
      />

      <GroupManager
        groups={groups}
        selectedGroup={selectedGroup}
        selectedSubgroup={selectedSubgroup}
        subgroups={subgroups}
        onGroupSelect={(groupId) => {
          setSelectedGroup(groupId);
          // Only clear subgroup selection if we're switching to a different group
          if (selectedGroup !== groupId) {
            setSelectedSubgroup(null);
          }
        }}
        onSubgroupSelect={handleSubgroupSelect}
        onAddGroup={() => setShowNewGroupDialog(true)}
        onAddSubgroup={handleCreateSubgroup}
        onDeleteGroup={handleDeleteGroup}
        onReorderGroups={handleReorderGroups}
        onReorderSubgroups={handleReorderSubgroups}
        onMoveSubgroup={handleMoveSubgroup}
        onConvertGroupToSubgroup={handleConvertGroupToSubgroup}
        onConvertSubgroupToGroup={handleConvertSubgroupToGroup}
      />

      <DraggableNoteList
        notes={filteredNotes}
        onNoteClick={handleNoteClick}
        onNotesReorder={handleNotesReorder}
        onDelete={handleDeleteNote}
      />
      
      <Dialog open={showSettings} onOpenChange={setShowSettings}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>Profile & Settings</DialogTitle>
          </DialogHeader>
          <ProfileSettings />
        </DialogContent>
      </Dialog>

      <Dialog open={showNewGroupDialog} onOpenChange={setShowNewGroupDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Group</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 p-4">
            <Input
              placeholder="Group name..."
              value={newGroupName}
              onChange={(e) => setNewGroupName(e.target.value)}
            />
            <Button onClick={handleCreateGroup}>Create Group</Button>
          </div>
        </DialogContent>
      </Dialog>
      
      <Menu />
    </div>
  );
};

export default Home;
